/**
 * Frontend Sentry integration — v7.4
 *
 * Captures exceptions + reportClientError helper.
 * Wired to VITE_SENTRY_DSN env var. No-op when not configured.
 */
import * as Sentry from "@sentry/react";

const DSN = (import.meta as any).env?.VITE_SENTRY_DSN || "";
const SAMPLE_RATE = Number((import.meta as any).env?.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.1);

let initialized = false;

export function initSentry() {
  if (initialized || !DSN) return;
  try {
    Sentry.init({
      dsn: DSN,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: SAMPLE_RATE,
      environment: (import.meta as any).env?.MODE || "production",
      // Don't send PII by default
      sendDefaultPii: false,
      // Better performance in healthcare ops
      beforeSendTransaction(event) {
        // Filter out noisy health checks
        if (event.transaction?.includes("/health")) return null;
        return event;
      },
    });
    initialized = true;
    // eslint-disable-next-line no-console
    console.info("[sentry] initialized", { dsn: DSN.slice(0, 40) + "…" });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[sentry] init failed", e);
  }
}

export function captureError(err: Error | unknown, context?: Record<string, any>) {
  if (initialized) {
    Sentry.captureException(err, { extra: context });
  } else {
    // eslint-disable-next-line no-console
    console.error("[client-error]", err, context);
  }
}

export function captureMessage(msg: string, level: "info" | "warning" | "error" = "info", context?: Record<string, any>) {
  if (initialized) {
    Sentry.captureMessage(msg, { level, extra: context });
  } else {
    // eslint-disable-next-line no-console
    console.log(`[client-${level}]`, msg, context);
  }
}

export function isSentryEnabled() {
  return initialized && !!DSN;
}